/**
 * Hotels Listing Slice
 *
 * Manages accumulated hotel listing state for "View More" functionality.
 * When action_type is "Search", replaces hotels list.
 * When action_type is "View-More", appends new hotels to existing list.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  HotelListingItemCard,
  HotelsListingTemplateData,
} from '@/api/contracts/templates/hotels-template-schemas/hotels-listing-schemas';
import {
  setActiveTemplate,
  updateTemplateFromStream,
} from '@/features/chat/redux/slices/templateSlice';

// ============================================
// State Types
// ============================================

export interface HotelsListingState {
  /** Accumulated hotels from all searches/view-more actions */
  hotels: HotelListingItemCard[];
  /** Total count of hotels available (from API) */
  totalCount: number;
  /** Whether more hotels can be loaded */
  hasMore: boolean;
  /** Loading state for view more action */
  isLoadingMore: boolean;
  /** Current search context ID to detect new searches */
  currentSearchContextId: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialListingState: HotelsListingState = {
  hotels: [],
  totalCount: 0,
  hasMore: false,
  isLoadingMore: false,
  currentSearchContextId: null,
};

// ============================================
// Payload Types
// ============================================

export interface UpdateListingPayload {
  hotels: HotelListingItemCard[];
  totalCount: number;
  actionType: 'Search' | 'View-More';
  /** Context ID to detect new searches (e.g., listing_deeplink or myraMsgId) */
  searchContextId?: string | undefined;
  /** Whether more hotels can be fetched (from pagination info or resourceContextData) */
  hasMore?: boolean | undefined;
}

// ============================================
// Slice
// ============================================

const listingSlice = createSlice({
  name: 'hotels/listing',
  initialState: initialListingState,
  reducers: {
    /**
     * Update hotels listing based on action type
     * - Search: Replace hotels list (new search)
     * - View-More: Append hotels to existing list
     */
    updateHotelsListing: (state, action: PayloadAction<UpdateListingPayload>) => {
      const {
        hotels,
        totalCount,
        actionType,
        searchContextId,
        hasMore: hasMoreFromPayload,
      } = action.payload;

      if (actionType === 'Search') {
        // New search - replace hotels list
        state.hotels = hotels;
        state.totalCount = totalCount;
        state.currentSearchContextId = searchContextId ?? null;
      } else if (actionType === 'View-More') {
        // View more - append hotels to existing list (avoid duplicates by ID)
        const existingIds = new Set(state.hotels.map((h) => h.id));
        const newHotels = hotels.filter((h) => !existingIds.has(h.id));
        state.hotels = [...state.hotels, ...newHotels];
        // totalCount might be updated from API
        if (totalCount > 0) {
          state.totalCount = totalCount;
        }
      }

      // Update hasMore: use explicit flag if provided, otherwise infer from counts
      // If hasMore is explicitly provided (from pagination info), use it
      // Otherwise, assume there's more if we received a full batch of hotels (10+)
      if (hasMoreFromPayload !== undefined) {
        state.hasMore = hasMoreFromPayload;
      } else {
        // Heuristic: if we received 10 or more hotels, there might be more
        // This is a fallback when explicit hasMore is not provided
        state.hasMore = hotels.length >= 10;
      }
      state.isLoadingMore = false;
    },

    /**
     * Set loading state for view more action
     */
    setLoadingMore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },

    /**
     * Clear hotels listing state
     */
    clearHotelsListing: (state) => {
      state.hotels = [];
      state.totalCount = 0;
      state.hasMore = false;
      state.isLoadingMore = false;
      state.currentSearchContextId = null;
    },

    /**
     * Reset to initial state
     */
    resetListingState: () => initialListingState,
  },
  extraReducers: (builder) => {
    /**
     * Shared handler for hotels-listing template data.
     * Determines Search vs View-More from searchContext.action_type,
     * then replaces or appends hotels accordingly.
     */
    const handleListingData = (
      state: HotelsListingState,
      listingData: HotelsListingTemplateData,
    ) => {
      const searchContext = listingData.searchContext;
      const actionType =
        (searchContext?.['action_type'] as string | undefined) === 'View-More'
          ? 'View-More'
          : 'Search';

      const pagination = searchContext?.['pagination'] as Record<string, unknown> | undefined;
      const limit = pagination?.['limit'] as number | undefined;
      const hasMore = limit !== undefined && listingData.hotels.length >= limit;
      const deeplink = searchContext?.['listing_deeplink'];
      const searchContextId = typeof deeplink === 'string' ? deeplink : undefined;

      if (actionType === 'Search') {
        state.hotels = listingData.hotels;
        state.totalCount = listingData.totalCount;
        state.currentSearchContextId = searchContextId ?? null;
      } else {
        const existingIds = new Set(state.hotels.map((h) => h.id));
        const newHotels = listingData.hotels.filter((h) => !existingIds.has(h.id));
        state.hotels = [...state.hotels, ...newHotels];
        if (listingData.totalCount > 0) {
          state.totalCount = listingData.totalCount;
        }
      }

      state.hasMore = hasMore;
      state.isLoadingMore = false;
    };

    // Live streaming: accumulate hotels when CB streams a listing response
    builder.addCase(updateTemplateFromStream, (state, action) => {
      if (action.payload.activeTemplate !== 'hotels-listing' || !action.payload.templateData)
        return;
      handleListingData(state, action.payload.templateData as HotelsListingTemplateData);
    });

    // History reload / card click: restore listing state from persisted template data
    builder.addCase(setActiveTemplate, (state, action) => {
      if (action.payload.template !== 'hotels-listing' || !action.payload.data) return;
      handleListingData(state, action.payload.data as HotelsListingTemplateData);
    });
  },
});

// ============================================
// Actions
// ============================================

export const { updateHotelsListing, setLoadingMore, clearHotelsListing, resetListingState } =
  listingSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const listingReducer = listingSlice.reducer;
